/*
 * PLAYBACK SHARING: THE SNIPPET (design: skydive_backend/docs/PLAYBACK_SHARE_DESIGN.md, Chris's decisions of 2026-10-23). A snippet is a short stretch of one day, FROZEN at the moment it is made: the visits that were on the
 * site in that stretch, the stage as the owner had it (which pages are in which boxes) and the display options it opens with. Everything here is a plain function over plain data, so the privacy rules are tested on their own:
 *   - no identifier travels: sessions are RE-KEYED (s1, s2, ...), so a snippet cannot be joined to anything else, and only the fields the stage draws are kept (no IP address, city, region, full referrer, device id, events);
 *   - the stretch is capped, and it is rebased to start at 0, so it reveals nothing about the rest of the day beyond when it was.
 */
import { createRouteTally, routesToWire, WireRoute } from "../util/routeTally";
export const SHARE_VERSION = 1;
export const MIN_SHARE_WINDOW_MS = 30 * 1000;
export const DEFAULT_SHARE_WINDOW_MS = 15 * 60 * 1000;
// a whole day at most: the cap exists so a clip cannot be longer than the day it was cut from, not because a day is too big (size is bounded by playback's own thinning of a busy day, see docs/PLAYBACK_SHARE_DESIGN.md)
export const MAX_SHARE_WINDOW_MS = 24 * 60 * 60 * 1000;
export const EXPIRY_CHOICES_DAYS = [7, 14, 30] as const;
export const DEFAULT_EXPIRY_DAYS = 14;
const DAY_MS = 86_400_000;

export interface ShareOptions {
  /** how the historical dock opens: cluster, rows, bars or list */
  layout: string;
  flowLines: boolean;
  flowStyle: string;
  icons: boolean;
  hideUnknown: boolean;
  labels: boolean;
  order: "asc" | "desc";
  /** how a visitor closes: fly, dim, shrink, pop, sink or vanish (clips made before the six styles have `dimLeaving` or nothing) */
  leaveStyle?: string;
  dimLeaving?: boolean;
  /** the real-time filter the owner had (INITIAL_FILTERS' menuType; absent in older clips: No Filter), and the rest of what was on screen (absent in older clips: the app's defaults) */
  filter?: string;
  burst?: boolean;
  pageTotals?: boolean;
  /** how many flow ribbons: the busiest 10, 20 or all (0) */
  ribbonCount?: number;
  commonPath?: boolean;
  /** the boxes stay where the owner put them: no reordering, no removing, no automatic sorting (absent in older clips: not locked) */
  lockBoxes?: boolean;
  /** may the recipient change these (their changes stay in THEIR browser) */
  allowViewChange: boolean;
}

export interface SharePage {
  name: string;
  selected: boolean;
  slotIndex: number | null;
}

export interface ShareNote {
  /** ms since the start of the stretch */
  at: number;
  text: string;
}

/**
 * The day BEFORE the stretch, as the dock counts it: how many visits had already left, and for each thing the dock can group by (country, browser, ...) how many of them had each value and how many of those had
 * converted. Counts only: a visit cannot be rebuilt from them, and one value's count says nothing about another field. `value` null = the visit had no value (the dock's "no data" group).
 */
export interface PriorDay {
  total: number;
  converted: number;
  /** field name -> [value or null, visits, converted][] */
  margins: Record<string, Array<[string | null, number, number]>>;
}

/**
 * The day BEFORE the stretch, as the flow lines count it: how many visits landed on each page, moved from one page to another, and left from each page, before the stretch began. By page name; a page that is not
 * one of the owner's boxes is "" ("All other pages"), so no page name leaves that the clip does not already show.
 */
export interface PriorFlows {
  arrivals: Array<[string, number]>;
  moves: Array<[string, string, number]>;
  departures: Array<[string, number]>;
}

export interface ShareSnapshot {
  version: number;
  /** the link's secret. Empty for a link made on the server and listed later: the server keeps only its hash, and the browser that made it remembers it (shareTokenMemory.ts) */
  token: string;
  /** a link on the server is named by this (never by its token) when it is renewed or turned off */
  id?: string;
  /** how many times the link was opened (a link on the server) */
  views?: number;
  label: string;
  siteName: string;
  createdAt: number;
  expiresAt: number;
  revokedAt?: number;
  /** made only to preview what a recipient will see: never listed, and cleared away soon after */
  draft?: boolean;
  /** absolute start and end of the stretch (ms since epoch) */
  startMs: number;
  endMs: number;
  /** the real getPlaybackTimeline answer's actions, rebased so the stretch starts at t = 0 */
  actions: any[];
  /** the day BEFORE the stretch: visits that had already left by its start, as counts per dock group (no per-visit data), so the dock opens with the day's totals so far */
  prior?: PriorDay;
  /** the day before the stretch as the flow lines / ribbons count it, so they open with the whole day's traffic and not only the clip's */
  priorFlows?: PriorFlows;
  /** the most common route's tally for the day before the stretch, so the red line opens with the day's route (the stretch's own visits are added as it plays) */
  priorRoutes?: WireRoute[];
  sampled: boolean;
  pages: SharePage[];
  /** pages that were conversion goals */
  goalUrls: string[];
  notes: ShareNote[];
  options: ShareOptions;
}

/** the stretch to share: never shorter than a moment, never longer than the cap, never ending before it starts */
export const clampWindow = (startMs: number, endMs: number): { startMs: number; endMs: number } => {
  const start = Number.isFinite(startMs) ? startMs : 0;
  let end = Number.isFinite(endMs) ? endMs : start + DEFAULT_SHARE_WINDOW_MS;
  if (end < start) end = start + DEFAULT_SHARE_WINDOW_MS;
  if (end - start < MIN_SHARE_WINDOW_MS) end = start + MIN_SHARE_WINDOW_MS;
  if (end - start > MAX_SHARE_WINDOW_MS) end = start + MAX_SHARE_WINDOW_MS;

  return { startMs: start, endMs: end };
};

/** the fields the stage and the hover cards draw, nothing that identifies a person or a device */
const KEPT_FIELDS = [
  "country_name", "country_code", "os", "browser", "device", "traffic_source", "search_engine", "social_network", "visitor_type", "visitor_category", "current_url_name", "goalReached", "goalUrl",
] as const;

/** a referrer as a host only: no path, no query string (they can carry a search phrase, an email address, a token); a direct visit keeps "/" */
export const referrerHost = (raw: unknown): string => {
  if (typeof raw !== "string" || raw === "" || raw === "/") return "/";
  try {
    return new URL(raw).hostname.replace(/^www\./, "") || "/";
  } catch {
    return "/";
  }
};

export const redactSession = (session: any, newId: string, createdAtRebased: number): any => {
  const out: any = { _id: newId, visitor_id: newId, createdAt: createdAtRebased, session_end_time: null, events: [], referrer: referrerHost(session?.referrer) };
  for (const field of KEPT_FIELDS) if (session && session[field] !== undefined && session[field] !== null) out[field] = session[field];

  return out;
};

/**
 * Cuts a day's timeline down to a stretch. `actions` are the getPlaybackTimeline answer's, `t` in ms from `dayStartMs`. The result starts at t = 0 and holds, for every visit that overlaps the stretch:
 *   - one that was ALREADY on the site at the start: an arrival at 0 on the page it was on at that moment;
 *   - one that arrived inside it: its arrival, rebased;
 * then each page view and departure that falls inside, rebased. A visit that had left before the start, or arrives after the end, is not in it. Sessions are re-keyed and redacted.
 */
export const sliceTimeline = (actions: any[], dayStartMs: number, startMs: number, endMs: number): any[] => {
  const from = startMs - dayStartMs;
  const to = endMs - dayStartMs;
  const bySession = new Map<string, any[]>();
  const order: string[] = [];
  for (const action of actions || []) {
    const id = action.type === "arrival" ? action.session?._id : action.sessionId;
    if (!id) continue;
    if (!bySession.has(id)) { bySession.set(id, []); order.push(id); }
    bySession.get(id)!.push(action);
  }
  const out: any[] = [];
  let n = 0;
  for (const id of order) {
    const mine = bySession.get(id)!;
    const arrival = mine.find((a) => a.type === "arrival");
    if (!arrival) continue;
    const departure = mine.find((a) => a.type === "departure");
    if (arrival.t > to) continue;                                   // arrives after the end
    if (departure && departure.t < from) continue;                  // had left before the start
    const newId = `s${++n}`;
    const views = mine.filter((a) => a.type === "pageview").sort((a, b) => a.t - b.t);
    const onSiteAtStart = arrival.t < from;
    const pageAtStart = onSiteAtStart ? [...views].reverse().find((v) => v.t <= from)?.pathName ?? arrival.session?.current_url_name : arrival.session?.current_url_name;
    const arrivalT = onSiteAtStart ? 0 : arrival.t - from;
    out.push({ type: "arrival", t: arrivalT, session: redactSession({ ...arrival.session, current_url_name: pageAtStart, goalReached: false }, newId, arrivalT) });
    for (const view of views) {
      if (view.t < from && onSiteAtStart) continue;                 // before the start: folded into where it was at the start
      if (view.t > to) continue;
      if (view.t < from) continue;
      out.push({ type: "pageview", t: Math.max(0, view.t - from), sessionId: newId, pathName: view.pathName, ...(view.goalReached ? { goalReached: true } : {}) });
    }
    if (departure && departure.t <= to) out.push({ type: "departure", t: Math.max(0, departure.t - from), sessionId: newId });
  }

  return out.sort((a, b) => a.t - b.t);
};

/** every characteristic the dock can group by (clusterService's filters) */
export const PRIOR_FIELDS = ["country_name", "os", "browser", "device", "traffic_source", "search_engine", "social_network", "referrer", "visitor_category", "visitor_type"] as const;

/**
 * The day so far, for the dock: every visit that had LEFT before the stretch began, counted per value of each characteristic the dock groups by. The referrer is cut to its host like everywhere else in a clip. A
 * visit that converted by the time it left is counted as converted.
 */
export const priorFromTimeline = (actions: any[], dayStartMs: number, startMs: number): PriorDay => {
  const from = startMs - dayStartMs;
  const arrivals = new Map<string, any>();
  const left = new Set<string>();
  const converted = new Set<string>();
  for (const a of actions || []) {
    if (a.type === "arrival") arrivals.set(a.session?._id, a);
    else if (a.type === "departure" && a.t < from) left.add(a.sessionId);
    else if (a.type === "pageview" && a.goalReached && a.t < from) converted.add(a.sessionId);
  }
  const tally: Record<string, Map<string | null, [number, number]>> = {};
  PRIOR_FIELDS.forEach((f) => { tally[f] = new Map(); });
  let total = 0;
  let conv = 0;
  left.forEach((id) => {
    const arrival = arrivals.get(id);
    if (!arrival) return;
    const did = converted.has(id) ? 1 : 0;
    total += 1;
    conv += did;
    for (const f of PRIOR_FIELDS) {
      const raw = f === "referrer" ? (arrival.session?.referrer ? referrerHost(arrival.session.referrer) : null) : arrival.session?.[f];
      const value = typeof raw === "string" && raw !== "" ? raw : null;
      const cell = tally[f].get(value) || [0, 0];
      cell[0] += 1;
      cell[1] += did;
      tally[f].set(value, cell);
    }
  });
  const margins: PriorDay["margins"] = {};
  PRIOR_FIELDS.forEach((f) => { margins[f] = Array.from(tally[f].entries()).map(([value, [n, g]]) => [value, n, g] as [string | null, number, number]); });

  return { total, converted: conv, margins };
};

/**
 * The day so far for the flow lines: every landing, every page-to-page move and every departure that happened before the stretch began (a visit that is still there at the start counts its landing and moves up to
 * then, exactly as playing through the day would have). Pages outside `boxes` are folded into "".
 */
export const priorFlowsFromTimeline = (actions: any[], dayStartMs: number, startMs: number, boxes: Set<string>): PriorFlows => {
  const from = startMs - dayStartMs;
  const name = (page: unknown): string => (typeof page === "string" && boxes.has(page) ? page : "");
  const at = new Map<string, string>();
  const arrivals = new Map<string, number>();
  const moves = new Map<string, number>();
  const departures = new Map<string, number>();
  const bump = (m: Map<string, number>, key: string) => m.set(key, (m.get(key) || 0) + 1);
  const sorted = [...(actions || [])].sort((a, b) => a.t - b.t);
  for (const a of sorted) {
    if (a.t >= from) break;
    if (a.type === "arrival") {
      const page = name(a.session?.current_url_name);
      at.set(String(a.session?._id), page);
      bump(arrivals, page);
    } else if (a.type === "pageview") {
      const id = String(a.sessionId);
      if (!at.has(id)) continue;
      const to = name(a.pathName);
      bump(moves, `${at.get(id)}\u0000${to}`);
      at.set(id, to);
    } else if (a.type === "departure") {
      const id = String(a.sessionId);
      if (!at.has(id)) continue;
      bump(departures, at.get(id) as string);
      at.delete(id);
    }
  }

  return {
    arrivals: Array.from(arrivals.entries()),
    moves: Array.from(moves.entries()).map(([k, n]) => { const [f, t] = k.split("\u0000"); return [f, t, n] as [string, string, number]; }),
    departures: Array.from(departures.entries()),
  };
};

/** the most common route's tally for the day before the stretch (the visits that had ENDED by its start), the busiest MAX_PRIOR_ROUTES routes of it: the red line's day-so-far */
export const MAX_PRIOR_ROUTES = 400;
export const OTHER_PAGES_NAME = "All other pages";
export const priorRoutesFromTimeline = (actions: any[], dayStartMs: number, startMs: number, boxes: Set<string>): WireRoute[] => {
  const from = startMs - dayStartMs;
  // like priorFlowsFromTimeline: only the owner's box pages are named, every other page is folded into "All other pages" (a recipient is never told the name of a page that is not on the owner's boxes)
  const name = (page: unknown): string => (typeof page === "string" && boxes.has(page) ? page : OTHER_PAGES_NAME);
  const tally = createRouteTally();
  const sorted = [...(actions || [])].sort((a, b) => a.t - b.t);
  for (const a of sorted) {
    if (a.t >= from) break;
    if (a.type === "arrival") tally.arrive(String(a.session?._id), name(a.session?.current_url_name));
    else if (a.type === "pageview") tally.move(String(a.sessionId), name(a.pathName));
    else if (a.type === "departure") tally.leave(String(a.sessionId));
  }

  return routesToWire(tally.routes(), MAX_PRIOR_ROUTES);
};

/** 128 random bits, URL-safe: a link nobody can guess */
export const newToken = (): string => {
  const bytes = new Uint8Array(16);
  // fail CLOSED: a link's whole protection is that it cannot be guessed, so there is no weaker fallback for a browser without a secure random source
  if (!globalThis.crypto || typeof globalThis.crypto.getRandomValues !== "function") throw new Error("no secure random source: a share link cannot be made here");
  globalThis.crypto.getRandomValues(bytes);
  let bin = "";
  bytes.forEach((b) => { bin += String.fromCharCode(b); });

  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const expiryFor = (createdAt: number, days: number): number => createdAt + Math.max(1, Math.min(90, Math.round(days))) * DAY_MS;

export type ShareState = "live" | "expired" | "revoked";
export const shareState = (snapshot: Pick<ShareSnapshot, "expiresAt" | "revokedAt">, nowMs: number): ShareState => (snapshot.revokedAt ? "revoked" : nowMs >= snapshot.expiresAt ? "expired" : "live");

/** a Renew keeps the link and moves the date out from NOW (not from the old date), and brings a lapsed link back; a revoked one stays revoked */
export const renewedExpiry = (nowMs: number, days: number): number => expiryFor(nowMs, days);

/** what names a link when it is renewed or turned off: its server id, or (a link kept in this browser) its token */
export const shareKey = (s: Pick<ShareSnapshot, "id" | "token">): string => s.id || s.token;

export const shareLink = (origin: string, token: string): string => `${origin.replace(/\/$/, "")}/s/${token}`;

/** a site's name as people read it: a host is shown without "https://" and "www." (Chris, 2026-10-24: remove them on all addresses in the app); a name that is not a web address is left as it is */
export const plainSiteName = (name: string): string => String(name || "").replace(/^https?:\/\//i, "").replace(/^www\./i, "");
